import { useEffect, useState } from 'react';
import { createRoute, redirect, Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as EncodersLayout } from './_layout';
import { Layout } from '@/client/layouts/layout';
import type { EncoderWithClient } from '@/client/store/encodersStore';
import { useEncoderWSSStore } from '@/client/store/encoderWSSStore';
import { useAlertStore } from '@/client/store/alertStore';
import { useLiveStreamsStore } from '@/client/store/liveStreamsStore';
import { AlertCard } from '@/client/components/alert-card';
import { EncoderMonitorCard } from '@/client/components/encoder/EncoderMonitorCard';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { JsonSyntaxHighlight } from '@/client/components/ui/json-syntax-highlight';
import { ArrowLeft, ArrowRight, Radio, Play, Stethoscope, Loader2, ChevronDown, ChevronRight, Video, Music, Clock, Gauge, Copy, Check, Captions, Zap, AlertTriangle, AlertCircle, Info, ScrollText } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { formatBitrate, formatResolution, formatFrameRate, formatFileSize, formatRelativeTime } from '@/client/lib/format';
import { getClientInfraNotes, type ClientInfraNotes } from '@/client/client-infra';
import type { TriageSession, ProbeResult, EncoderSampleData, StreamDeepProbeResult } from '@shared/types';

export const Route = createRoute({
  getParentRoute: () => EncodersLayout,
  path: 'encoders/$encoderID',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: EncoderDetailComponent,
});

function EncoderStatusBadge({ status, wssOnline }: { status: number | null; wssOnline?: boolean }) {
  // WSS status overrides DB status when available
  if (wssOnline !== undefined) {
    if (wssOnline) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Online</Badge>;
    } else {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Offline</Badge>;
    }
  }

  // Fall back to DB status
  if (status === null) {
    return <Badge variant="outline" className="text-muted-foreground">Unknown</Badge>;
  }
  switch (status) {
    case 0:
      return <Badge variant="outline" className="text-muted-foreground">Offline</Badge>;
    case 1:
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Online</Badge>;
    case 2:
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Streaming</Badge>;
    default:
      return <Badge variant="outline" className="text-muted-foreground">Unknown ({status})</Badge>;
  }
}

function CopyableField({ label, value }: { label: string; value: string | null }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center py-1.5">
      <span className="text-sm text-muted-foreground min-w-[160px]">{label}</span>
      {value ? (
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 transition-colors cursor-pointer"
          title="Click to copy"
        >
          <span className="font-mono text-sm truncate" title={value}>
            {value}
          </span>
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          )}
        </button>
      ) : (
        <span className="font-mono text-sm">-</span>
      )}
    </div>
  );
}

function parseStreamInfo(streamInfoJson: string | null): EncoderSampleData | null {
  if (!streamInfoJson) return null;
  try {
    return JSON.parse(streamInfoJson) as EncoderSampleData;
  } catch {
    return null;
  }
}

function formatTriageDuration(startedAt: number): string {
  const diff = Date.now() - startedAt;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function MetricCard({ label, value, icon: Icon }: { label: string; value: string; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="bg-muted/50 rounded-lg p-3">
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="font-medium text-sm">{value}</div>
    </div>
  );
}

function ProbeResultDisplay({ result }: { result: ProbeResult }) {
  const [showRaw, setShowRaw] = useState(false);

  if (!result.success) {
    return (
      <div className="p-4 rounded-md text-sm bg-red-50 border border-red-200">
        <p className="text-red-600">{result.error}</p>
      </div>
    );
  }

  const { metadata, raw, probeTimeMs } = result;

  return (
    <div className="space-y-4">
      {/* Video Details */}
      {metadata?.video && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Video className="h-4 w-4 text-muted-foreground" />
            <h5 className="font-medium text-sm">Video Stream</h5>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            <div>
              <dt className="text-muted-foreground text-xs">Codec</dt>
              <dd className="font-mono">{metadata.video.codec}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Profile / Level</dt>
              <dd className="font-mono">
                {metadata.video.profile || metadata.video.level
                  ? `${metadata.video.profile || '-'} ${metadata.video.level || ''}`
                  : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Resolution</dt>
              <dd className="font-mono">{formatResolution(metadata.video.width, metadata.video.height)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Pixel Format</dt>
              <dd className="font-mono">{metadata.video.pixelFormat || '-'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Frame Rate</dt>
              <dd className="font-mono">{metadata.video.frameRate ? `${metadata.video.frameRate.toFixed(2)} fps` : '-'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Bitrate</dt>
              <dd className="font-mono">{metadata.video.bitrate ? formatBitrate(metadata.video.bitrate) : '-'}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* Audio Details */}
      {metadata?.audio && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Music className="h-4 w-4 text-muted-foreground" />
            <h5 className="font-medium text-sm">Audio Stream</h5>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            <div>
              <dt className="text-muted-foreground text-xs">Codec</dt>
              <dd className="font-mono">{metadata.audio.codec}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Sample Rate</dt>
              <dd className="font-mono">{(metadata.audio.sampleRate / 1000).toFixed(1)} kHz</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Channels</dt>
              <dd className="font-mono">{metadata.audio.channels}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Bitrate</dt>
              <dd className="font-mono">{metadata.audio.bitrate ? formatBitrate(metadata.audio.bitrate) : '-'}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* File Size (for VOD) */}
      {metadata?.size && (
        <div className="text-sm text-muted-foreground">
          File Size: {formatFileSize(metadata.size)}
        </div>
      )}

      {/* Collapsible Raw JSON */}
      {raw && (
        <div className="border rounded-lg">
          <button
            onClick={() => setShowRaw(!showRaw)}
            className="w-full flex items-center gap-2 p-3 text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
          >
            {showRaw ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Raw ffprobe output
          </button>
          {showRaw && (
            <div className="p-4 pt-0 overflow-x-auto max-w-full">
              <JsonSyntaxHighlight json={JSON.stringify(raw, null, 2)} />
            </div>
          )}
        </div>
      )}

      {/* Probe Time */}
      {probeTimeMs && (
        <p className="text-xs text-muted-foreground">Probed in {probeTimeMs}ms</p>
      )}
    </div>
  );
}

function DeepProbeResultDisplay({ result }: { result: StreamDeepProbeResult }) {
  if (!result.success) {
    return (
      <div className="p-4 rounded-md text-sm bg-red-50 border border-red-200">
        <p className="text-red-600">{result.error}</p>
      </div>
    );
  }

  const { bitrateAnalysis, gopAnalysis, warnings, sampleDurationSec, probeTimeMs } = result;

  return (
    <div className="space-y-4">
      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Bitrate Mode"
          value={bitrateAnalysis?.mode ?? '-'}
        />
        <MetricCard
          label="CV (Variance)"
          value={bitrateAnalysis?.cv !== undefined ? bitrateAnalysis.cv.toFixed(3) : '-'}
        />
        <MetricCard
          label="GOP Size"
          value={gopAnalysis?.avgGopSize !== undefined ? `${gopAnalysis.avgGopSize} frames` : '-'}
        />
        <MetricCard
          label="GOP Duration"
          value={gopAnalysis?.avgGopDurationSec !== undefined ? `${gopAnalysis.avgGopDurationSec}s` : '-'}
        />
      </div>

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h5 className="font-medium text-sm text-amber-800">Warnings</h5>
          </div>
          <ul className="text-sm text-amber-700 space-y-1">
            {warnings.map((warning, i) => (
              <li key={i}>• {warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Detailed Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {bitrateAnalysis && (
          <div className="border rounded-lg p-4">
            <h5 className="font-medium text-sm mb-3">Bitrate Analysis</h5>
            <dl className="text-sm space-y-1">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Mode</dt>
                <dd className="font-mono">{bitrateAnalysis.mode}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Coefficient of Variation</dt>
                <dd className="font-mono">{bitrateAnalysis.cv.toFixed(3)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Avg Packet Size</dt>
                <dd className="font-mono">{bitrateAnalysis.avgPacketSize.toLocaleString()} bytes</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Std Dev</dt>
                <dd className="font-mono">{bitrateAnalysis.stddevPacketSize.toLocaleString()} bytes</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Packets Analyzed</dt>
                <dd className="font-mono">{bitrateAnalysis.packetCount.toLocaleString()}</dd>
              </div>
            </dl>
          </div>
        )}

        {gopAnalysis && (
          <div className="border rounded-lg p-4">
            <h5 className="font-medium text-sm mb-3">GOP Analysis</h5>
            <dl className="text-sm space-y-1">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Avg GOP Size</dt>
                <dd className="font-mono">{gopAnalysis.avgGopSize} frames</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Avg GOP Duration</dt>
                <dd className="font-mono">{gopAnalysis.avgGopDurationSec}s</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">I-Frames Found</dt>
                <dd className="font-mono">{gopAnalysis.iFrameCount}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Frames Analyzed</dt>
                <dd className="font-mono">{gopAnalysis.frameCount.toLocaleString()}</dd>
              </div>
              {gopAnalysis.gopSizes.length > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">GOP Range</dt>
                  <dd className="font-mono">
                    {Math.min(...gopAnalysis.gopSizes)}-{Math.max(...gopAnalysis.gopSizes)} frames
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>

      {/* Probe Info */}
      <p className="text-xs text-muted-foreground">
        Sampled {sampleDurationSec}s of stream data in {probeTimeMs}ms
      </p>
    </div>
  );
}

function InfraNotesPanel({ notes }: { notes: ClientInfraNotes }) {
  return (
    <div className="rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <h5 className="font-medium text-blue-900 dark:text-blue-100">Infrastructure Notes</h5>
      </div>
      <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
        {notes.protocol && (
          <div>
            <span className="font-medium">Protocol:</span>{' '}
            <span className="font-mono bg-blue-100 dark:bg-blue-900/50 px-1 rounded">{notes.protocol.actual}</span>
            {' '}<span className="text-blue-600 dark:text-blue-400">({notes.protocol.note})</span>
          </div>
        )}
        {notes.portPattern && (
          <div>
            <span className="font-medium">Port pattern:</span>{' '}
            <span className="font-mono bg-blue-100 dark:bg-blue-900/50 px-1 rounded">{notes.portPattern}</span>
          </div>
        )}
        {notes.configLocation && (
          <div className="space-y-1">
            <div>
              <span className="font-medium">Config location:</span>{' '}
              {notes.configLocation.url ? (
                <a
                  href={notes.configLocation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 dark:text-blue-400 underline hover:text-blue-900 dark:hover:text-blue-300"
                >
                  {notes.configLocation.system}
                </a>
              ) : (
                notes.configLocation.system
              )}
              {' → '}{notes.configLocation.path}
            </div>
            <div>
              <span className="font-medium">Credentials:</span>{' '}
              {notes.configLocation.credentials}
            </div>
          </div>
        )}
        {notes.notes && notes.notes.length > 0 && (
          <ul className="mt-2 space-y-1">
            {notes.notes.map((note, i) => (
              <li key={i}>
                <span className="font-medium">{note.title}:</span>{' '}
                {note.description}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EncoderDetailComponent() {
  const { encoderID } = Route.useParams();
  const { getAccessTokenSilently } = useAuth0();
  // Subscribe to WSS store for real-time online/offline status from Scaledrone
  const wssEncoders = useEncoderWSSStore((state) => state.encoders);
  const wssRecord = wssEncoders.get(encoderID);

  // Get live event for this encoder (if any)
  const liveStreams = useLiveStreamsStore((state) => state.liveStreams);
  const liveEvent = liveStreams.find((s) => s.encoderID === encoderID);

  // Subscribe to alert store for active alerts on this encoder
  const getAlertsByEncoder = useAlertStore((state) => state.getAlertsByEncoder);
  const triageSessions = useAlertStore((state) => state.triageSessions);
  const muteAlert = useAlertStore((state) => state.muteAlert);
  const unmuteAlert = useAlertStore((state) => state.unmuteAlert);
  const isAlertMuted = useAlertStore((state) => state.isAlertMuted);
  const encoderAlerts = getAlertsByEncoder(encoderID);
  const isInTriage = triageSessions.some((s) => s.encoderId === encoderID);
  const [encoder, setEncoder] = useState<EncoderWithClient | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);
  const [deepProbeLoading, setDeepProbeLoading] = useState(false);
  const [deepProbeResult, setDeepProbeResult] = useState<StreamDeepProbeResult | null>(null);

  // Triage state
  const [triageSession, setTriageSession] = useState<TriageSession | null>(null);
  const [triageLoading, setTriageLoading] = useState(false);

  // Raw JSON panel state
  const [showRawJson, setShowRawJson] = useState(false);

  // Parse streamInfo once
  const streamData = encoder ? parseStreamInfo(encoder.streamInfo?.streamInfo ?? null) : null;

  // Get client-specific infrastructure notes (if any)
  const infraNotes = encoder ? getClientInfraNotes(encoder.clientID) : null;

  // Copy handlers
  const handleCopyRawJson = async () => {
    if (!encoder) return;
    const rawData = {
      ...encoder,
      streamInfo: encoder.streamInfo?.streamInfo ? JSON.parse(encoder.streamInfo.streamInfo) : null,
    };
    await navigator.clipboard.writeText(JSON.stringify(rawData, null, 2));
  };

  const handleCopyFullState = async () => {
    if (!encoder) return;
    const fullState = {
      encoder: {
        ...encoder,
        streamInfo: encoder.streamInfo?.streamInfo ? JSON.parse(encoder.streamInfo.streamInfo) : null,
      },
      parsedStreamData: streamData,
      probeResult: probeResult,
      triageSession: triageSession,
    };
    await navigator.clipboard.writeText(JSON.stringify(fullState, null, 2));
  };

  // Fetch encoder directly from API (no caching - real-time status)
  useEffect(() => {
    const loadEncoder = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE_URL}/api/encoders/${encoderID}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Encoder not found');
          } else {
            setError(`Failed to fetch encoder: ${response.statusText}`);
          }
          return;
        }

        const data = await response.json();
        setEncoder(data.encoder);
      } catch (err) {
        console.error('Error fetching encoder:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadEncoder();
  }, [getAccessTokenSilently, encoderID]);

  const handleProbe = async () => {
    if (!encoder) return;

    setProbeLoading(true);
    setProbeResult(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/encoders/probe?inputUri=${encodeURIComponent(encoder.inputURI)}&encoderID=${encodeURIComponent(encoder.encoderID)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      setProbeResult(result);
    } catch (err) {
      setProbeResult({
        success: false,
        inputUri: encoder.inputURI,
        error: err instanceof Error ? err.message : 'Failed to probe encoder',
      });
    } finally {
      setProbeLoading(false);
    }
  };

  const handleDeepProbe = async () => {
    if (!encoder) return;

    setDeepProbeLoading(true);
    setDeepProbeResult(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/encoders/probe/deep?inputUri=${encodeURIComponent(encoder.inputURI)}&encoderID=${encodeURIComponent(encoder.encoderID)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      setDeepProbeResult(result);
    } catch (err) {
      setDeepProbeResult({
        success: false,
        inputUri: encoder.inputURI,
        error: err instanceof Error ? err.message : 'Failed to deep probe encoder',
        sampleDurationSec: 0,
        probeTimeMs: 0,
      });
    } finally {
      setDeepProbeLoading(false);
    }
  };

  // Fetch triage session status on load
  useEffect(() => {
    const fetchTriageStatus = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${API_BASE_URL}/api/encoders/${encoderID}/triage`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setTriageSession(data.session);
        } else if (response.status === 404) {
          setTriageSession(null);
        }
      } catch (err) {
        console.error('Error fetching triage status:', err);
      }
    };

    if (encoderID) {
      fetchTriageStatus();
    }
  }, [encoderID, getAccessTokenSilently]);

  const handleStartTriage = async () => {
    setTriageLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/encoders/${encoderID}/triage`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setTriageSession(data.session);
      }
    } catch (err) {
      console.error('Error starting triage:', err);
    } finally {
      setTriageLoading(false);
    }
  };

  const handleStopTriage = async () => {
    setTriageLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await fetch(
        `${API_BASE_URL}/api/encoders/${encoderID}/triage`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTriageSession(null);
    } catch (err) {
      console.error('Error stopping triage:', err);
    } finally {
      setTriageLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div>
          <p>Loading encoder...</p>
        </div>
      </Layout>
    );
  }

  if (error || !encoder) {
    return (
      <Layout>
        <div>
          <div className="mb-4 flex items-center gap-4">
            <Link to="/encoders">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h3 className="text-2xl font-semibold">{error === 'Encoder not found' ? 'Encoder Not Found' : 'Error'}</h3>
          </div>
          <p>{error || `The encoder "${encoderID}" could not be found.`}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="rounded-md border p-6">
          {/* Header Row */}
          <div className="flex items-center gap-4 mb-2">
            <Link to="/encoders">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-2xl font-semibold">{encoder.simpleName}</h3>
                <EncoderStatusBadge status={encoder.status} wssOnline={wssRecord?.online} />
                {/* Show live event badge and link if encoder is streaming */}
                {liveEvent && (
                  <>
                    <Badge variant="outline" className="bg-red-600 text-white border-red-600 dark:bg-red-600 dark:text-white">
                      <Play className="h-3 w-3 mr-1 fill-current" />
                      Live
                    </Badge>
                    <Link
                      to="/events/$eventId"
                      params={{ eventId: `${liveEvent.eventID}` }}
                      className="text-sm text-primary hover:underline"
                    >
                      View Stream Info: {liveEvent.eventID}
                    </Link>
                  </>
                )}
                {/* Show flapping warning if WSS detects instability */}
                {wssRecord && wssRecord.state !== 'normal' && (
                  <Badge
                    variant="outline"
                    className={wssRecord.state === 'alert'
                      ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                    }
                    title={`${wssRecord.events.length} connect/disconnect events in the last 60 seconds`}
                  >
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {wssRecord.state === 'alert' ? 'Flapping' : 'Unstable'}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                <Link
                  to="/clients/$clientID"
                  params={{ clientID: encoder.clientID }}
                  className="hover:underline"
                >
                  {encoder.clientName}
                </Link>
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={handleCopyRawJson}>
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copy Raw JSON
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopyFullState}>
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copy Full State
            </Button>
            <Button
              variant={showRawJson ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowRawJson(!showRawJson)}
            >
              {showRawJson ? <ChevronDown className="h-3.5 w-3.5 mr-1.5" /> : <ChevronRight className="h-3.5 w-3.5 mr-1.5" />}
              {showRawJson ? 'Hide' : 'View'} Raw JSON
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link
                to="/pipeline-logs"
                search={{ searchType: 'encoderID', searchValue: encoderID }}
              >
                <ScrollText className="h-3.5 w-3.5 mr-1.5" />
                Pipeline Logs
              </Link>
            </Button>
          </div>

          {/* Encoder Details */}
          <div className="border-t pt-4 space-y-1">
            <CopyableField label="Stream Name" value={encoder.streamName} />
            <CopyableField label="Encoder ID" value={encoder.encoderID} />
            <CopyableField label="Input URI" value={encoder.inputURI} />
            <CopyableField label="Ingest Instance ID" value={encoder.ingInstanceID} />
            <CopyableField label="WC Stream ID" value={encoder.wcStreamID} />
            <div className="flex items-center py-1.5">
              <span className="text-sm text-muted-foreground min-w-[160px]">Last Communication</span>
              <span className="text-sm">
                {encoder.lastCommunication
                  ? `${new Date(encoder.lastCommunication).toLocaleString()} (${formatRelativeTime(encoder.lastCommunication)})`
                  : '-'}
              </span>
            </div>
          </div>

          {/* Raw JSON Panel (collapsible) */}
          {showRawJson && (
            <div className="border-t pt-4 mt-4">
              <h5 className="text-sm font-medium mb-2">Raw Encoder Data</h5>
              <div className="bg-muted p-4 rounded-md text-sm overflow-x-auto max-w-full">
                <JsonSyntaxHighlight json={JSON.stringify({
                  ...encoder,
                  streamInfo: encoder.streamInfo?.streamInfo ? JSON.parse(encoder.streamInfo.streamInfo) : null,
                }, null, 2)} />
              </div>
            </div>
          )}
        </div>

        {/* Infrastructure Notes - client-specific setup documentation */}
        {infraNotes && <InfraNotesPanel notes={infraNotes} />}

        {/* Live Status Section - only when online with streamInfo */}
        {streamData && (encoder.status === 1 || encoder.status === 2) && (
          <div className="rounded-md border p-6">
            <h4 className="text-lg font-medium mb-4">Live Stream Status</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard
                label="Resolution"
                value={streamData.width && streamData.height ? `${streamData.width}×${streamData.height}` : '-'}
              />
              <MetricCard
                label="Bitrate"
                value={streamData.bitrate ? formatBitrate(streamData.bitrate) : '-'}
                icon={Gauge}
              />
              <MetricCard
                label="Frame Rate"
                value={streamData.real_frame_rate ? formatFrameRate(streamData.real_frame_rate) : '-'}
                icon={Clock}
              />
              <MetricCard
                label="Captions"
                value={streamData.inputHasCaptions === undefined ? '-' : streamData.inputHasCaptions ? 'Detected' : 'None'}
                icon={Captions}
              />
            </div>
            {streamData.dimensions && streamData.dimensions.length > 0 && (
              <p className="text-sm text-muted-foreground mt-3">
                Transcode outputs: {streamData.dimensions.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Connection Monitoring Section - only show if there are issues */}
        {wssRecord && wssRecord.state !== 'normal' && (
          <div className="rounded-md border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="h-5 w-5 text-blue-600" />
              <h4 className="text-lg font-medium">Connection Monitoring</h4>
            </div>
            <EncoderMonitorCard encoder={wssRecord} />
          </div>
        )}

        {/* Active Alerts Section - only show if there are alerts */}
        {encoderAlerts.length > 0 && (
          <div className="rounded-md border p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h4 className="text-lg font-medium">Active Alerts</h4>
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                {encoderAlerts.length}
              </Badge>
            </div>
            <div className="space-y-3">
              {encoderAlerts.map((alert) => (
                <AlertCard
                  key={`${alert.encoderId}-${alert.alertType}`}
                  alert={alert}
                  isInTriage={isInTriage}
                  onPinToTriage={handleStartTriage}
                  isMuted={isAlertMuted(alert.encoderId, alert.alertType)}
                  onMute={muteAlert}
                  onUnmute={unmuteAlert}
                />
              ))}
            </div>
          </div>
        )}

        {/* Stream Diagnostics */}
        <div className="rounded-md border p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium">Stream Diagnostics</h4>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleProbe}
                disabled={probeLoading || deepProbeLoading}
              >
                <Radio className={`h-4 w-4 mr-2 ${probeLoading ? 'animate-pulse' : ''}`} />
                {probeLoading ? 'Probing...' : 'Probe Stream'}
              </Button>
              <Button
                variant="outline"
                onClick={handleDeepProbe}
                disabled={probeLoading || deepProbeLoading}
              >
                <Zap className={`h-4 w-4 mr-2 ${deepProbeLoading ? 'animate-pulse' : ''}`} />
                {deepProbeLoading ? 'Analyzing...' : 'Deep Probe (~15s)'}
              </Button>
            </div>
          </div>

          {/* Standard Probe Results */}
          {probeResult && (
            <div className="mb-6">
              <h5 className="text-sm font-medium mb-3 text-muted-foreground">Standard Probe</h5>
              <ProbeResultDisplay result={probeResult} />
            </div>
          )}

          {/* Deep Probe Results */}
          {deepProbeResult && (
            <div className="mb-6">
              <h5 className="text-sm font-medium mb-3 text-muted-foreground">Deep Analysis (CBR/VBR, GOP)</h5>
              <DeepProbeResultDisplay result={deepProbeResult} />
            </div>
          )}

          {!probeResult && !deepProbeResult && (
            <p className="text-sm text-muted-foreground">
              <strong>Probe Stream</strong> - Quick metadata check (codec, resolution, bitrate)<br />
              <strong>Deep Probe</strong> - Samples 10s of stream to analyze CBR/VBR mode and GOP structure
            </p>
          )}
        </div>

        {/* Encoder Triage */}
        <div className="rounded-md border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/encoders/triage" className="inline-flex items-center gap-1 hover:text-foreground group hover:text-zinc-500 text-lg font-medium underline">
                Encoder Triage <ArrowRight className="h-3 w-3 hover:text-zinc-500" />
              </Link>
              {triageSession && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700">
                  In Triage for {formatTriageDuration(triageSession.startedAt)}
                </Badge>
              )}
            </div>
            {triageSession ? (
              <Button
                variant="destructive"
                onClick={handleStopTriage}
                disabled={triageLoading}
              >
                {triageLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Stethoscope className="h-4 w-4 mr-2" />
                )}
                Stop Triage
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleStartTriage}
                disabled={triageLoading}
              >
                {triageLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Stethoscope className="h-4 w-4 mr-2" />
                )}
                Start Triage
              </Button>
            )}
          </div>

          {triageSession ? (
            <div className="text-sm">
              <p className="text-muted-foreground mb-2">
                This encoder is being monitored. Samples are being collected and can be viewed on the{' '}
                <Link to="/encoders/triage" className="text-primary hover:underline">
                  Encoder Triage page
                </Link>.
              </p>
              {triageSession.notes && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Notes:</span> {triageSession.notes}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Start a triage session to monitor this encoder's stream metrics in real-time.
              Samples will be collected and persisted for analysis.
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}
